import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Enum for postMessage types used in iframe communication
 */
export enum PostMessageType {
  INSPECT_MODE = 'INSPECT_MODE',
  FOLLOWING_DATA = 'FOLLOWING_DATA',
  RECORDING_SESSION_ENDED = 'RECORDING_SESSION_ENDED',
}

/**
 * Message type for inspect mode postMessage communication
 */
export interface InspectModeMessage {
  type: PostMessageType.INSPECT_MODE;
  enabled: boolean;
  /** Optional target user ID to follow in inspect mode */
  targetUserId?: string;
  /** Optional tab ID for the target user */
  targetTabId?: string;
  /** Optional target room ID for spectator to join (breakout room) */
  targetRoomId?: string;
}

/**
 * Message type for following data postMessage communication
 */
export interface FollowingDataMessage {
  type: PostMessageType.FOLLOWING_DATA;
  enabled: boolean;
  /** Target user ID to follow */
  targetUserId?: string;
  /** Tab ID for the target user */
  targetTabId?: string;
}

/**
 * Message type for recording session ended postMessage communication.
 * When received, the frontend should stop following the current target user.
 */
export interface RecordingSessionEndedMessage {
  type: PostMessageType.RECORDING_SESSION_ENDED;
}

/**
 * Combined state for target user and tab in inspect mode
 */
export interface InspectModeTargetData {
  userId: string | null;
  tabId: string | null;
}

/**
 * Repository service for managing inspect mode state.
 * Inspect mode is used when the app is embedded in an iframe and the parent
 * window wants to observe/inspect a user's session without interacting.
 *
 * This service is intentionally lightweight with minimal dependencies to avoid
 * circular dependency issues that can occur with SessionSharedDataService.
 */
@Injectable({
  providedIn: 'root',
})
export class InspectModeRepository {
  // ============================================
  // Inspect Mode State (set via postMessage from parent iframe)
  // Shared with FOLLOWING_DATA messages for target user/tab tracking
  // ============================================
  private readonly _isInspectMode$ = new BehaviorSubject<boolean>(this.isInspectModeEnabledFromUrl());
  private readonly _inspectModeTargetData$ = new BehaviorSubject<InspectModeTargetData>({
    userId: null,
    tabId: null,
  });
  private readonly _inspectModeTargetRoomId$ = new BehaviorSubject<string | null>(null);
  private readonly _startAutoFollow$ = new BehaviorSubject<boolean>(false);

  /** Observable for inspect mode state - components should subscribe to this */
  public readonly isInspectMode$ = this._isInspectMode$.asObservable();

  /** Observable for the target user ID and tab ID in inspect mode */
  public readonly inspectModeTargetData$ = this._inspectModeTargetData$.asObservable();

  /** Observable for the target room ID in inspect mode (for spectator to join breakout room) */
  public readonly inspectModeTargetRoomId$ = this._inspectModeTargetRoomId$.asObservable();

  /** Observable to trigger auto-follow logic when room is set */
  public readonly startAutoFollow$ = this._startAutoFollow$.asObservable();

  /** Synchronous getter for inspect mode state */
  public get isInspectMode(): boolean {
    return this._isInspectMode$.getValue();
  }

  /** Synchronous getter for target user ID in inspect mode */
  public get inspectModeTargetUserId(): string | null {
    return this._inspectModeTargetData$.getValue().userId;
  }

  /** Synchronous getter for target tab ID in inspect mode */
  public get inspectModeTargetTabId(): string | null {
    return this._inspectModeTargetData$.getValue().tabId;
  }

  /** Synchronous getter for target data in inspect mode */
  public get inspectModeTargetData(): InspectModeTargetData {
    return this._inspectModeTargetData$.getValue();
  }

  /** Synchronous getter for target room ID in inspect mode */
  public get inspectModeTargetRoomId(): string | null {
    return this._inspectModeTargetRoomId$.getValue();
  }

  /**
   * Checks if inspect mode is enabled via URL query parameter.
   * This method checks the URL directly and is useful for pre-initialization checks
   * before postMessage communication is established.
   * @returns true if the URL contains 'inspectWithIframe=true' query parameter
   */
  private isInspectModeEnabledFromUrl(): boolean {
    return window.location.search.includes('inspectWithIframe=true');
  }

  /**
   * Triggers the auto-follow logic. Called when the spectator room is assigned.
   */
  public triggerAutoFollow(): void {
    this._startAutoFollow$.next(true);
  }

  /**
   * Sets the inspect mode state. Called by AppComponent when receiving postMessage from parent iframe.
   * @param enabled Whether inspect mode is enabled
   * @param targetUserId Optional target user ID to follow
   * @param targetTabId Optional tab ID for the target user
   * @param targetRoomId Optional breakout room ID for spectator to join
   */
  public setInspectMode(
    enabled: boolean,
    targetRoomId?: string,
    activeSpaceId?: string,
  ): void {
    this._isInspectMode$.next(enabled);

    // Convert targetRoomId to proper room UID format
    // Main room: "MainRoom" -> "{spaceId}:MainRoom"
    // Breakout room: use as-is (already full UID)
    const roomUid =
      targetRoomId === 'MainRoom' && activeSpaceId
        ? `${activeSpaceId}:MainRoom`
        : targetRoomId;
    this._inspectModeTargetRoomId$.next(roomUid ?? null);
  }

  /**
   * Sets the following data state. Called by AppComponent when receiving postMessage from parent iframe.
   * Updates the shared target user ID and tab ID (same as inspect mode uses).
   * @param _enabled Whether following data is enabled (not stored, but used for logging)
   * @param targetUserId Optional target user ID to follow
   * @param targetTabId Optional tab ID for the target user
   */
  public setFollowingData(
    targetUserId?: string,
    targetTabId?: string,
  ): void {
    this._inspectModeTargetData$.next({
      userId: targetUserId ?? null,
      tabId: targetTabId ?? null,
    });
  }

  /**
   * Clears the current target user/tab for inspect-mode auto-follow.
   * Consumers (e.g. session) should stop following when this is invoked.
   */
  public stopFollowingCurrentTarget(): void {
    this._inspectModeTargetData$.next({
      userId: null,
      tabId: null,
    });
  }
}
